import {confirmModal} from "../../../../confirmModal";
import {toLonLat} from "ol/proj";
import {nanoid} from "nanoid";
import {createFeatureFromCoordinates} from "../../../../map/utilities";
import {Select} from "ol/interaction";
import {drawInventoryLocationStyle} from "../../../../map/styles/inventory-locations";

export const InventoryLocationInteractionButtonState = {
    OFF: 'off',
    ON: 'on',
    DISABLED: 'disabled',
}

export const InventoryLocationInteractionMode = {
    DRAW: 'draw',
    EDIT: 'edit',
    DELETE: 'delete',
}

export const InventoryLocationInteractionFeatureType = {
    SINGLE: 'single',
    MULTI: 'multi'
}

export class InventoryLocationInteraction {
    /**
     * @type {InventoryLocationInteractionsHandler|null}
     */
    handler = null;

    name = null;

    /**
     * @type {jQuery|null}
     */
    toggleButton = null;

    /**
     * @type {jQuery|null}
     */
    deleteButton = null;

    /**
     * @type {jQuery|null}
     */
    container = null;

    dictionary = {
        [InventoryLocationInteractionMode.DRAW]: 'Rysuj',
        [InventoryLocationInteractionMode.EDIT]: 'Edytuj',
        deleteConfirm: 'Czy na pewno chcesz to usunąć?',
    };

    /**
     * @type {Array<Feature>}
     */
    features = [];

    /**
     * @type {EpLayer}
     */
    epLayer = null;

    options = {
        featureType: InventoryLocationInteractionFeatureType.SINGLE
    }

    mode = null;

    /**
     * @type {Interaction|null}
     */
    olInteraction = null;

    snapInteractions = [];

    /**
     * @type {string|null}
     */
    currentId = null;

    /**
     * @type {Select}
     */
    select = null;

    enabled = false;

    /**
     * @param {string} name
     * @param {InventoryLocationInteractionsHandler} handler
     * @param {EpLayer} epLayer
     * @param options
     */
    constructor(name, handler, epLayer, options = {}) {
        this.options = {
            ...this.options,
            ...options
        }

        this.name = name;
        this.handler = handler;
        this.epLayer = epLayer;

        this.toggleButton = $(`[data-app-map_interaction_button="${this.name}"]`);
        if (!this.toggleButton.length) {
            this.toggleButton = null;
        }

        this.deleteButton = $(`[data-app-map_interaction_delete_button="${this.name}"]`);
        if (!this.deleteButton.length) {
            this.deleteButton = null;
        }

        if (this.options.featureType === InventoryLocationInteractionFeatureType.SINGLE) {
            this.container = $('body');
        } else {
            this.container = $(`[data-app-map_interaction_container="${this.name}"]`);
        }

        this.setupToggleButtonEventHandler();
        this.setupDeleteButtonEventHandler();

        if (this.options.featureType === InventoryLocationInteractionFeatureType.MULTI) {
            this.setupEditButtonEventHandler();
        }

        this.addSelect();
    }

    /**
     * Change delete button state
     * @param state
     */
    changeDeleteButtonState(state) {
        if (this.deleteButton === null) {
            return;
        }

        if (state === InventoryLocationInteractionButtonState.DISABLED) {
            this.deleteButton.attr('disabled', true);
            return;
        }

        this.deleteButton.attr('disabled', false);
    }

    toggleButtonOn(element) {
        element.addClass('btn-warning');
        element.removeClass('btn-primary');
    }

    toggleButtonOff(element) {
        element.addClass('btn-primary');
        element.removeClass('btn-warning');
    }

    /**
     * Change toggle button state
     * @param state
     * @param mode - If null passed, nothing will change
     */
    changeToggleButtonState(state = null, mode = null) {
        let toggleButton = this.toggleButton;
        let that = this;

        if(this.options.featureType === InventoryLocationInteractionFeatureType.MULTI) {
            if(mode === InventoryLocationInteractionMode.EDIT && this.currentId) {
                toggleButton = $(`[data-app-map_interaction_edit_button="${this.name}"][data-app-feature_id="${this.currentId}"]`)
            } else {
                $(`[data-app-map_interaction_edit_button="${this.name}"]`).each(function() {
                    that.toggleButtonOff($(this));
                })
            }
        }

        if (toggleButton === null) {
            return;
        }

        if (mode !== null) {
            toggleButton.text(this.dictionary[mode]);
        }

        if (state !== null) {
            if (state === InventoryLocationInteractionButtonState.DISABLED) {
                toggleButton.attr('disabled', true);
                toggleButton.removeClass('btn-warning');
                toggleButton.addClass('btn-primary');
                return;
            }

            toggleButton.attr('disabled', false);

            if (state === InventoryLocationInteractionButtonState.ON) {
                this.toggleButtonOn(toggleButton)
                return;
            }

            this.toggleButtonOff(toggleButton)
        }
    }

    toggleInteraction(id = null) {
        let interaction = this.handler.setCurrentInteraction(this);
        if (interaction === null) {
            return;
        }

        if (this.options.featureType === InventoryLocationInteractionFeatureType.SINGLE) {
            if (this.features.length > 0) {
                this.edit();
            } else {
                this.draw();
            }
        } else {
            if (id !== null) {
                this.currentId = id;
                this.edit();
            } else {
                this.draw();
            }
        }
    }

    setupToggleButtonEventHandler() {
        if (this.toggleButton === null) {
            return;
        }

        this.toggleButton.click(() => {
            this.toggleInteraction();
        })
    }

    setupEditButtonEventHandler() {
        let that = this;

        this.container.on('click', `[data-app-map_interaction_edit_button="${this.name}"]`, function () {
            let id = $(this).attr('data-app-feature_id');
            that.toggleInteraction(id);
        })
    }

    async toggleDelete(id) {
        if (await confirmModal(this.dictionary.deleteConfirm, {color: 'danger'})) {
            let feature = this.features[0];

            if (this.options.featureType === InventoryLocationInteractionFeatureType.MULTI) {
                feature = this.features.find(feature => feature.get('id') === id);
            }

            this.epLayer.getSource().removeFeature(feature);

            if (this.options.featureType === InventoryLocationInteractionFeatureType.SINGLE) {
                this.updateInput('', id);
            } else {
                this.getInput(id)?.remove();
            }

            this.features = this.features.filter(f => f.get('id') !== feature.get('id'));

            this.changeToggleButtonState(
                InventoryLocationInteractionButtonState.OFF
            );
            this.handler.setCurrentInteraction(null);

            this.toggleButtonTextDynamicUpdate();
            this.deleteButtonStateDynamicUpdate();

            this.onDelete(id);
            this.onChange();
        }
    }

    setupDeleteButtonEventHandler() {
        if (this.epLayer === null) {
            return;
        }

        let that = this;

        this.container.on('click', `[data-app-map_interaction_delete_button="${this.name}"]`, async function () {
            await that.toggleDelete($(this).attr('data-app-feature_id'));
        })
    }

    getInput(id = null) {
        if (this.options.featureType === InventoryLocationInteractionFeatureType.SINGLE) {
            return $(`[data-app-map_interaction_input="${this.name}"]`);
        }

        if (id === null) {
            return;
        }

        let input = $(`[data-app-map_interaction_input="${this.name}"][data-app-feature_id="${id}"]`);
        if (!input.length) {
            input = $(`
                <input type="hidden" name="coordinates_${this.name}[${id}]" data-app-map_interaction_input="${this.name}" data-app-feature_id="${id}" />
            `);
            $('#inputsContainer').append(input);
        }

        return input;
    }

    updateInput(value, id = null) {
        if (this.getInput(id)?.length) {
            this.getInput(id)
                .val(value)
                .change();
        }
    }

    toggleButtonTextDynamicUpdate() {
        this.changeToggleButtonState(
            null,
            this.features.length ? InventoryLocationInteractionMode.EDIT : InventoryLocationInteractionMode.DRAW
        )
    }

    deleteButtonStateDynamicUpdate() {
        if (this.options.featureType === InventoryLocationInteractionFeatureType.SINGLE) {
            if (this.features.length) {
                this.changeDeleteButtonState(InventoryLocationInteractionButtonState.ON);
            } else {
                this.changeDeleteButtonState(InventoryLocationInteractionButtonState.DISABLED);
            }
        }
    }

    addOlInteraction(interaction, withSnap = true) {
        this.olInteraction = interaction;
        this.handler.epMap.getOlMap().addInteraction(this.olInteraction);

        if (withSnap) {
            this.snapInteractions = [
                this.handler.epMap.addSnap(this.handler.epMap.sectors.getSource()),
                this.handler.epMap.addSnap(this.handler.epMap.boundaries.getSource()),
                this.handler.epMap.addSnap(this.epLayer.getSource())
            ]

            if (typeof this.handler.interactions.location != 'undefined') {
                this.snapInteractions = [
                    ...this.snapInteractions,
                    this.handler.epMap.addSnap(
                        this.handler.interactions.location.epLayer.getSource()
                    )
                ]
            }

            if (typeof this.handler.epMap['_locations'] != 'undefined') {
                this.snapInteractions = [
                    ...this.snapInteractions,
                    this.handler.epMap.addSnap(this.handler.epMap['_locations'].getSource())
                ]
            }

            for (let i of this.snapInteractions) {
                this.handler.epMap.getOlMap().addInteraction(i);
            }
        }
    }

    removeOlInteraction() {
        if (this.olInteraction) {
            this.handler.epMap.getOlMap().removeInteraction(this.olInteraction);
        }

        for (let i of this.snapInteractions) {
            this.handler.epMap.getOlMap().removeInteraction(i);
        }
    }

    getStringCoordinatesFromFeature = (feature) => {
        let coordinates = feature.getGeometry().getCoordinates();
        if (coordinates.length) {
            if (typeof coordinates[0][0] != 'object') {
                coordinates = feature.getGeometry().getCoordinates()
                    .map(el => toLonLat(el).join(',')).join(' ');
            } else {
                coordinates = feature.getGeometry().getCoordinates()
                    .map(arr => arr.map(el => toLonLat(el).join(',')).join(' '))[0];
            }
        }

        return coordinates;
    }

    /**
     * @param {Feature} feature
     * @param id
     */
    addFeature(feature, id = null) {
        this.features = [
            ...this.features,
            feature
        ]

        let coordinates = this.getStringCoordinatesFromFeature(feature);

        feature.set('type', this.name);
        feature.set('edited', 'true');
        feature.set('id', id === null ? nanoid() : id);

        this.epLayer.getSource().addFeature(feature);

        this.updateInput(coordinates, feature.get('id'));
        this.handler.mapUpdated();

        this.onChange();

        return feature;
    }

    updateFeature(feature) {
        let coordinates = this.getStringCoordinatesFromFeature(feature);

        this.updateInput(coordinates, feature.get('id'));
        this.handler.mapUpdated();

        this.onChange();
    }

    createFeatureFromInput(type = 'Polygon', style = null) {
        if (this.options.featureType === InventoryLocationInteractionFeatureType.SINGLE) {
            let input = this.getInput();
            if (input.length) {
                const coordinates = input.val();

                if (coordinates.length > 0) {
                    let feature = createFeatureFromCoordinates(coordinates, null, type);

                    if (style !== null) {
                        feature.setStyle(style);
                    }

                    this.addFeature(feature);
                }
            }
        } else {
            let inputs = $(`[data-app-map_interaction_input="${this.name}"]`);
            if (inputs.length) {
                for(let input of inputs) {
                    const coordinates = $(input).val();

                    if (coordinates.length > 0) {
                        let feature = createFeatureFromCoordinates(coordinates, null, type);

                        if (style !== null) {
                            feature.setStyle(style);
                        }

                        this.addFeature(feature, $(input).attr('data-app-feature_id'));
                    }
                }
            }
        }

        this.toggleButtonTextDynamicUpdate();
        this.deleteButtonStateDynamicUpdate();
    }

    draw() {
        this.toggleButtonTextDynamicUpdate();
        this.changeToggleButtonState(
            InventoryLocationInteractionButtonState.ON,
            InventoryLocationInteractionMode.DRAW
        )

        this.mode = InventoryLocationInteractionMode.DRAW;

        this.onChange();
    }

    getCurrentFeature(id = null) {
        if(!this.features.length) {
            return null;
        }

        let feature = this.features[0];
        if(this.options.featureType === InventoryLocationInteractionFeatureType.MULTI) {
            feature = this.features.find(f => f.get('id') === (id === null ? this.currentId : id));
        }

        return feature;
    }

    edit() {
        this.toggleButtonTextDynamicUpdate();
        this.changeToggleButtonState(
            InventoryLocationInteractionButtonState.ON,
            InventoryLocationInteractionMode.EDIT
        )

        this.mode = InventoryLocationInteractionMode.EDIT;

        this.select.getFeatures().clear();

        let feature = this.getCurrentFeature();
        if(feature) {
            this.select.getFeatures().push(feature);
        }

        this.onChange();
    }

    end(forced = false) {
        this.currentId = null;

        this.toggleButtonTextDynamicUpdate();
        this.deleteButtonStateDynamicUpdate();

        this.changeToggleButtonState(
            InventoryLocationInteractionButtonState.OFF,
        )

        this.removeOlInteraction();
        this.handler.epMap.helpTooltip.clear();

        this.mode = null;

        this.select.getFeatures().clear();

        this.onChange();
    }

    addSelect() {
        this.select = new Select({
            style: drawInventoryLocationStyle,
            layers: [this.epLayer.getLayer()],
            condition: () => false
        })

        this.handler.epMap.map.addInteraction(this.select);
    }

    hide() {
        this.handler.epMap.layerControl.handleVisibilityChange(this.epLayer, false)

        if(this.handler.currentModeInteraction?.name === this.name) {
            this.handler.setCurrentInteraction(this);
        }

        if(this.options.featureType === InventoryLocationInteractionFeatureType.MULTI) {
            $(`[data-app-map_interaction_container="${this.name}"]`).hide();
        }
    }

    show() {
        this.handler.epMap.layerControl.handleVisibilityChange(this.epLayer, true)

        if(this.options.featureType === InventoryLocationInteractionFeatureType.MULTI) {
            $(`[data-app-map_interaction_container="${this.name}"]`).show();
        }
    }

    updateAreaInfo() {}

    updateLengthInfo() {}

    setEnabled(enabled) {
        this.enabled = enabled;
    }

    onChange() {
        // disable districtId select
        let hasGeometry = false;

        for(let interaction in this.handler.interactions) {
            if(this.handler.interactions[interaction].features.length > 0) {
                hasGeometry = true;
                break;
            }
        }

        $('[name="district_id"]').attr('disabled', hasGeometry);
    }

    onDelete(id) {
        this.updateAreaInfo()
        this.updateLengthInfo()
    }
}